import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {EntityKey} from '../../entity/entity';
import {EntityBreak} from '../../entity/entity-break';
import {PersonLogin} from '../../person/person-login';
import {SelectionItem} from '../../selections/selection-item';
import {EntityBreakService} from '../../services/entity-break-service';
import {SelectionService} from '../../services/selection-service';
import {StringUtils} from '../../utils/string-utils';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogRef} from '@angular/material/dialog';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {FormsModule} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import { CurrencyPipe } from '@angular/common';
import {FactorService} from "../../services/factor.service";
import {BrandID} from "../../enums/brand-id";
import {Factor} from "../../model/factor";

@Component({
    selector: 'entity-break-dialog',
    templateUrl: './entity-break-dialog.component.html',
    styleUrls: ['./entity-break-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, FormsModule, MatProgressSpinner, MatDialogActions, CurrencyPipe]
})
export class EntityBreakDialogComponent {
  private readonly entityBreakService = inject(EntityBreakService);
  private readonly factorService = inject(FactorService);
  readonly dialogRef = inject<MatDialogRef<EntityBreakDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);


  public entityKey: EntityKey;
  private personLogin: PersonLogin;
  private brandID: BrandID;
  public readonly errorMessage = signal<string>(undefined!);
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly selectedBreak = signal<EntityBreak>(undefined!);
  public readonly entityBreaks = signal<EntityBreak[]>(undefined!);
  public readonly updatesApplied = signal<boolean>(false);
  public readonly activeFactors = signal<Factor[]>(undefined!);

  constructor
  () {
    const data = this.data;

    this.entityKey = data.entityKey as EntityKey;
    this.personLogin = data.personLogin as PersonLogin;
    this.brandID = data.brandID as BrandID;
    this.factorService.getActiveFactors(this.brandID).subscribe({
        next: result => {
          this.activeFactors.set(result);
          },
        error: error => {this.handleError(error)  }
      }
    );
    this.loadBreaks();
  }

  private loadBreaks(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.selectedBreak.set(null!);
    this.entityBreakService.getBreaks(this.entityKey).subscribe(results => {
      this.entityBreaks.set(results);
      this.isBusy.set(false);
    }, error => this.handleError(error));
  }

  public cancelUpdate(): void {
    this.selectedBreak.set(null!);
  }

  public selectBreak(entityBreak: EntityBreak) {
    this.selectedBreak.set(entityBreak);
  }

  public newBreak(): void {
    this.selectedBreak.set(new EntityBreak());
    this.selectedBreak().key = this.entityKey;
    this.selectedBreak().createUser = this.personLogin.person.userLogin;
    this.selectedBreak().createTimestamp = new Date();
  }

  public deleteBreak(entityBreak: EntityBreak): void {
    this.isBusy.set(true);
    this.entityBreakService.deleteBreak(entityBreak).subscribe(() => {
      this.updatesApplied.set(true);
      this.loadBreaks();
    }, error => this.handleError(error));
  }

  public saveBreak(): void {
    this.isBusy.set(true);
    this.entityBreakService.update(this.selectedBreak()).subscribe(() => {
      this.updatesApplied.set(true);
      this.loadBreaks();
    }, error => this.handleError(error));
  }

  public close(): void {
    this.dialogRef.close(this.updatesApplied());
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  factorChanged(factorCode: string): void {
    const LIFTGATE_CODE = 'LFTG';

    if(factorCode === LIFTGATE_CODE){
      this.selectedBreak().price = this.activeFactors().find((factor:Factor) => factor.code === factorCode)!.factorValue;
    }
  }
}
